/**
 * fromtFrostBridge — TypeScript bridge for Monero FROST WASM operations.
 *
 * The underlying WASM (`/fromt-frost/fromt_wasm.js` + `.wasm`) is built
 * from `frosty-lib/crates/fromt-wasm` and exports session-driven
 * primitives for FROST Ed25519 DKG, signing, key import, key-image
 * generation, and a CLSAG spend pipeline. We only need a small subset
 * for the WINBIT32 use cases:
 *
 *   1. **Bake-in at vault creation**: drive an in-tab multi-party DKG
 *      across N session instances so every share file leaves the
 *      Create Vault Wizard with its own Monero `KeyShareBundle`.
 *      Mirrors the Orchard bake-in flow.
 *
 *   2. **Bundle inspection**: read the binary `KeyShareBundle` format
 *      `[version:1][network:1][view_key:32][birthday:8][kp_len:4]
 *      [KeyPackage][pkp_len:4][PublicKeyPackage]` so callers can
 *      surface birthday / view-key / network without booting the
 *      WASM scanner.
 *
 *   3. **Address derivation** (later): `fromt_derive_address` returns
 *      the standard Monero address from a key share, used by the
 *      receive flow once Monero send/receive lands.
 *
 * Loading is deferred — the WASM is only fetched when the caller
 * actually invokes a function that needs it. This keeps cold-start
 * cheap for users who never touch Monero.
 */

// Cache-busted via `?v=<build>` — see src/utils/assetCacheBust.js for
// why these public/-served WASM modules need a per-build token.
import { withCacheBust } from '../../../../utils/assetCacheBust';

const FROMT_WASM_BASE = '/fromt-frost';
const FROMT_JS_PATH = `${FROMT_WASM_BASE}/fromt_wasm.js`;
const FROMT_WASM_PATH = `${FROMT_WASM_BASE}/fromt_wasm_bg.wasm`;

/** Network byte for Monero address prefix. Mainnet = 0x12 (18). */
export type FromtNetwork = 'mainnet' | 'testnet' | 'stagenet';

const NETWORK_BYTE: Record<FromtNetwork, number> = {
	mainnet: 0x12,
	testnet: 0x35,
	stagenet: 0x18,
};

export interface FromtPartyEntry {
	frostId: number;
	name: string;
}

export interface FromtKeyShareBundleHeader {
	version: number;
	networkByte: number;
	viewKey: Uint8Array; // 32 bytes
	birthday: bigint;
	keyPackageBytes: Uint8Array;
	publicKeyPackageBytes: Uint8Array;
}

export interface FromtDkgSessionHandle {
	feed(msg: Uint8Array): boolean;
	takeMsg(): Uint8Array | undefined;
	msgReceiver(msg: Uint8Array, index: number): string | undefined;
	result(): Uint8Array;
	free(): void;
}

interface FromtWasmExports {
	default: (path?: string | URL) => Promise<unknown>;
	fromtDkgSetupMsgNew: (
		max_signers: number,
		min_signers: number,
		parties_data: Uint8Array,
		network: number,
		birthday: bigint,
	) => Uint8Array;
	FromtDkgSession: {
		fromSetup(
			setup_bytes: Uint8Array,
			my_party_name: string,
			network: number,
			birthday: bigint,
		): FromtDkgSessionHandle;
	};
	// All derivation helpers are confirmed to ship in the published
	// `/fromt-frost/fromt_wasm.d.ts` — see that file for the full
	// surface. We keep them non-optional so callers don't have to
	// null-check on every use.
	fromt_derive_address: (key_share: Uint8Array) => string;
	fromt_derive_subaddress: (key_share: Uint8Array, account: number, index: number) => string;
	fromt_derive_view_key: (key_share: Uint8Array) => Uint8Array;
	fromt_derive_spend_pub_key: (key_share: Uint8Array) => Uint8Array;
	fromt_keyshare_birthday: (key_share: Uint8Array) => bigint;
	fromt_keyshare_network: (key_share: Uint8Array) => number;
	// Group size — `1` means a true 1-of-1 share where the
	// `signing_share` is the full Monero spend secret. Optional so
	// older WASM bundles that don't export it still load; callers
	// must guard with `typeof`.
	fromt_keyshare_max_signers?: (key_share: Uint8Array) => number;
	// Extract the 32-byte spend secret from a 1-of-1 bundle. Throws
	// on threshold bundles. Used by the receive panel to compute
	// the wallet's real key image for each LWS unspent output so we
	// can filter out outputs that have actually been spent.
	fromt_keyshare_spend_secret_singleparty?: (key_share: Uint8Array) => Uint8Array;
	// Direct key-image computation given the spend secret. Always
	// available from the underlying WASM (no MPC required), but
	// only useful in combination with the spend-secret extractor
	// above — Vultisig threshold wallets must go through the
	// `key_image_part1/part2` ceremony instead.
	fromt_compute_key_image?: (
		key_offset: Uint8Array,
		output_key: Uint8Array,
		spend_key: Uint8Array,
	) => Uint8Array;
	// ── Spend pipeline helpers (used by `moneroSpendCoordinator`) ──
	fromt_derive_key_offset: (
		view_secret_key: Uint8Array,
		tx_pub_key: Uint8Array,
		output_index: bigint,
	) => Uint8Array;
	fromt_derive_commitment_mask: (
		view_secret_key: Uint8Array,
		tx_pub_key: Uint8Array,
		output_index: bigint,
	) => Uint8Array;
	/** `C = mask*G + amount*H` — use for the real ring member. */
	fromt_pedersen_commitment?: (
		commitment_mask: Uint8Array,
		amount: bigint,
	) => Uint8Array;
	fromt_build_signable_tx: (
		key_share: Uint8Array,
		recipient: string,
		amount: bigint,
		fee_per_weight: bigint,
		fee_mask: bigint,
		inputs_data: Uint8Array,
		decoys_data: Uint8Array,
	) => Uint8Array;
	// Multi-output variant (commission/donation splits). Optional so
	// older WASM bundles that don't export it still typecheck and
	// load — call sites null-check before invoking.
	fromt_build_signable_tx_multi?: (
		key_share: Uint8Array,
		destinations_data: Uint8Array,
		fee_per_weight: bigint,
		fee_mask: bigint,
		inputs_data: Uint8Array,
		decoys_data: Uint8Array,
	) => Uint8Array;
	fromt_spend_preprocess: (
		key_share: Uint8Array,
		signable_tx: Uint8Array,
	) => { readonly handle_id: number; readonly preprocess: Uint8Array };
	fromt_spend_sign: (
		handle_id: number,
		preprocesses_map: Uint8Array,
	) => { readonly handle_id: number; readonly share: Uint8Array };
	fromt_spend_complete: (
		handle_id: number,
		shares_map: Uint8Array,
	) => Uint8Array;
	fromt_tx_hash: (raw_tx: Uint8Array) => Uint8Array;
	fromt_handle_free: (handle_id: number) => void;
	// ── Single-party bundle from a 32-byte seed (phrase mode) ──
	// Produces the same binary `KeyShareBundle` layout as the
	// multi-party DKG, so downstream consumers (`MoneroAccount`,
	// the spend pipeline, etc.) treat it identically. Optional
	// at the type level so older WASM bundles that pre-date the
	// helper still load — callers must null-check.
	fromt_singleparty_bundle_from_seed?: (
		seed: Uint8Array,
		network: number,
		birthday: bigint,
	) => Uint8Array;
}

let _initPromise: Promise<FromtWasmExports> | null = null;

/**
 * Lazily load `/fromt-frost/fromt_wasm.js` and instantiate the WASM.
 * Returns the cached module on subsequent calls.
 */
export async function initFromtFrostWasm(): Promise<FromtWasmExports> {
	if (_initPromise) return _initPromise;
	_initPromise = (async () => {
		// `webpackIgnore: true` keeps webpack from trying to resolve the
		// runtime URL at build time — `fromt-frost/*` lives in `public/`
		// and is fetched live, just like the Orchard FROST WASM.
		const mod = await import(/* webpackIgnore: true */ withCacheBust(FROMT_JS_PATH));
		const wasm = mod as unknown as FromtWasmExports;
		// `__wbg_init`-style default export accepts either a URL/Request
		// or undefined; passing the explicit path avoids the deprecation
		// warning emitted when wasm-pack-built JS tries to derive its own.
		await wasm.default(withCacheBust(FROMT_WASM_PATH));
		return wasm;
	})().catch((err) => {
		_initPromise = null;
		throw err;
	});
	return _initPromise;
}

/**
 * Encode a list of `(frostId, name)` parties into the binary
 * `parties_data` blob expected by the `fromt*SetupMsgNew` builders.
 *
 * Format (little-endian):
 *   [count:u16][ frost_id:u16  name_len:u16  name:bytes ] × count
 */
export function encodeFromtPartiesData(parties: FromtPartyEntry[]): Uint8Array {
	if (!parties.length) throw new Error('encodeFromtPartiesData: no parties supplied');
	const enc = new TextEncoder();
	const nameBufs = parties.map(p => enc.encode(p.name));
	let total = 2;
	for (const buf of nameBufs) total += 2 + 2 + buf.length;
	const out = new Uint8Array(total);
	const view = new DataView(out.buffer);
	let pos = 0;
	view.setUint16(pos, parties.length, true); pos += 2;
	for (let i = 0; i < parties.length; i++) {
		view.setUint16(pos, parties[i].frostId, true); pos += 2;
		view.setUint16(pos, nameBufs[i].length, true); pos += 2;
		out.set(nameBufs[i], pos);
		pos += nameBufs[i].length;
	}
	return out;
}

/**
 * Map our friendly network name to the byte the WASM expects in
 * `KeyShareBundle.network`.
 */
export function networkByte(network: FromtNetwork): number {
	const b = NETWORK_BYTE[network];
	if (b === undefined) {
		throw new Error(`Unknown Monero network "${network}"`);
	}
	return b;
}

/**
 * Decode the binary `KeyShareBundle` produced by the WASM. We don't
 * need the inner `KeyPackage` / `PublicKeyPackage` here — the WASM
 * unpacks them on demand. This helper just exposes the lightweight
 * fields (network, view-key, birthday) so the UI can show summary
 * information without booting the scanner.
 */
export function parseKeyShareBundleHeader(bundle: Uint8Array): FromtKeyShareBundleHeader {
	if (bundle.length < 1 + 1 + 32 + 8 + 4) {
		throw new Error('KeyShareBundle too short');
	}
	const view = new DataView(bundle.buffer, bundle.byteOffset, bundle.byteLength);
	let pos = 0;
	const version = view.getUint8(pos); pos += 1;
	if (version !== 1 && version !== 2) {
		throw new Error(`Unsupported KeyShareBundle version: ${version}`);
	}
	const networkByteVal = view.getUint8(pos); pos += 1;
	const viewKey = bundle.slice(pos, pos + 32); pos += 32;
	const birthday = version >= 2 ? view.getBigUint64(pos, true) : 0n;
	if (version >= 2) pos += 8;
	const kpLen = view.getUint32(pos, true); pos += 4;
	if (pos + kpLen + 4 > bundle.length) throw new Error('KeyShareBundle: KeyPackage truncated');
	const keyPackageBytes = bundle.slice(pos, pos + kpLen); pos += kpLen;
	const pkpLen = view.getUint32(pos, true); pos += 4;
	if (pos + pkpLen > bundle.length) throw new Error('KeyShareBundle: PublicKeyPackage truncated');
	const publicKeyPackageBytes = bundle.slice(pos, pos + pkpLen);
	return {
		version,
		networkByte: networkByteVal,
		viewKey,
		birthday,
		keyPackageBytes,
		publicKeyPackageBytes,
	};
}
